import { detach, getParent, getParentOfType, getRoot, isRoot, types,
// types,
 } from 'mobx-state-tree';
import { v4 } from 'uuid';
import * as Yup from 'yup';
import { putContent } from './S3Client';
import { navStore } from './NavStore';
import { fieldValidator } from './YupAdapter';
import schema, { generateType, JsonType, updateFieldsGenerator, } from './Schema';
import { Item, ItemSection, getNewItem, } from './ItemModel';
import { checkAccess, slugify } from './itemCommon';
import flattenSections from './flattenSections';
/**
 * A course is made up of sections.  Each section can:
 * 1. contain sub-Sections
 * 2. Point to a content item directly
 * 3. Point to an array of content items (this is just another type of content)
 * 4. Optionally include a template that shows how to lay out the sub-sections
 *    and content items
 *
 * If we orgnanize this way every section has 0 or more sections and 0 or 1 content
 * items.
 *
 */
export const moveItem = (array, currentIndex, newIndex) => {
    // const indexes = [currentIndex, newIndex].sort(); // Sort the indixes
    // array.splice(indexes[0], 2, array[indexes[1]], array[indexes[0]]);
    // Replace from lowest index, two elements, reverting the order
    array.splice(newIndex, 0, array.splice(currentIndex, 1)[0]);
    // this.splice(to, 0, this.splice(from, 1)[0]);
};
export const CourseItem = generateType(schema.getModel('courseItem') /* , overrideMap */);
export const Instructor = generateType(schema.getModel('instructor'))
    .actions((self) => ({
    // This is an auto-generated example action.
    log() {
        console.log(JSON.stringify(self));
    },
}));
// const AiWizardContentModel = types.model({
//   threadID: types.string,
//   titles: types.array(types.string),
//   outline: types.array(types.string),
//   ideation: types.map(types.string),
// }).named('AiWizardContentModel');
const CourseSectionModel = types.model({
    id: types.identifier,
    name: types.string,
    points: JsonType,
    script: types.maybeNull(types.string),
    // parts: types.array(types.union(
    //   types.late((): IAnyModelType => CourseSection),
    //   types.late(() => CourseLesson)
    // ))
    sections: types.optional(types.array(types.late(() => CourseSectionModel)), []),
    type: types.enumeration('SectionType', ['block', 'video', 'audio', 'quiz', 'article', 'pdf',
        'download', 'container', 'course', 'knowledgeCheck', 'microcast', 'certification', 'scorm', 'html', 'typeform']),
    description: types.optional(types.string, ''),
    item: types.maybeNull(CourseItem),
}).named('CourseSectionModel')
    .views((self) => ({
    /**
     *
     * @returns the parent of this section - either the course if its top level
     * or the section that contains this one.
     */
    getParent() {
        return getParent(self, 2);
    },
    canHaveSubSections() {
        return self.item === null;
    },
    canHaveItems() {
        return self.item == null;
        // section.sections == null || section.sections.length === 0;
    },
    canDelete() {
        return (!self.sections || self.sections.length === 0);
    },
    findSection(id) {
        if (self.id === id) {
            return self;
        }
        return self.sections.find((s) => s.findSection(id));
    },
}))
    .actions((self) => ({
    addChange() {
        // eslint-disable-next-line @typescript-eslint/no-use-before-define
        getParentOfType(self, Course).addChange();
    },
    addSubSection(name) {
        const newSection = CourseSectionModel.create({
            id: v4(),
            name,
            description: '',
            type: 'container',
        });
        self.sections.push(newSection);
        this.addChange();
        return newSection;
    },
    addSection(name, type) {
        const item = getNewItem(name, type);
        const newSection = CourseSectionModel.create({
            id: v4(),
            name,
            type,
            description: '',
            item,
        });
        self.sections.push(newSection);
        this.addChange();
        return newSection;
    },
    moveSubSection(subSection, newIndex) {
        const currentIndex = self.sections.indexOf(subSection);
        const adjustedNewIndex = newIndex > currentIndex ? newIndex - 1 : newIndex;
        const tempArray = self.sections.slice();
        moveItem(tempArray, currentIndex, adjustedNewIndex);
        self.sections.replace(tempArray);
        this.addChange();
    },
    /**
     * Returns the subSection from the section and returns it.
     * Section is matched by ID.
     * @param subSection
     */
    removeSubSection(toRemove) {
        detach(toRemove);
        this.addChange();
    },
    /**
     * Adds a subsection into a section at the specified index.  If index is negative
     * it is added at the end.
     * @param subSection
     * @param atIndex
     */
    insertSubSection(subSection, atIndex) {
        if (atIndex < 0) {
            self.sections.push(subSection);
        }
        else {
            self.sections.splice(atIndex, 0, subSection);
        }
        this.addChange();
    },
    /**
     *
     * @param fields JSON, it is a flat list of key value pairs
     */
    // updateFields: updateFieldsGenerator(self),
    updateFields(fields, formData, store) {
        if (fields.includes('item.microcast')) {
            const microcast = store.getOrLoad('microcast', formData.item.microcast);
            fields.push('name');
            formData.name = microcast.name;
        }
        updateFieldsGenerator(self)(fields, formData);
    },
}))
    .views((self) => ({
    isCourse() {
        return false;
    },
    isLesson() {
        return [
            'block',
            'video',
            'audio',
            'article',
            'pdf',
            'microcast',
            'scorm',
            'html',
            'typeform',
        ].includes(self.type);
    },
    isQuiz() {
        return self.type === 'quiz';
    },
    isDownload() {
        return self.type === 'download';
    },
    calcLessonCount() {
        if (!self.sections) {
            return 0;
        }
        // count this section if its a lesson plus any lessons from sub-sections
        return self.sections.reduce((total, section) => (total + section.calcLessonCount()), self.isLesson() ? 1 : 0);
    },
    calcQuizCount() {
        if (!self.sections) {
            return 0;
        }
        // count this section if its a lesson plus any lessons from sub-sections
        return self.sections.reduce((total, section) => total + section.calcQuizCount(), self.isQuiz() ? 1 : 0);
    },
    isCertification() {
        return self.type === 'certification';
    },
    hasCertification() {
        if (!self.sections) {
            return 0;
        }
        // count this section if its a lesson plus any lessons from sub-sections
        return self.sections.reduce((total, section) => total + section.hasCertification(), self.isCertification() ? 1 : 0);
    },
    hasMaterials() {
        if (!self.sections) {
            return 0;
        }
        // count this section if its a lesson plus any lessons from sub-sections
        return self.sections.reduce((total, section) => total + section.hasMaterials(), self.isDownload() ? 1 : 0);
    },
}));
export const CourseSection = types
    .compose(ItemSection, CourseSectionModel)
    .named('CourseSection');
/**
 * TODO: A lot of the section related methods are copied from the CourseSection - refactor
 * to remove duplication
 */
// const overrideMap = new Map();
// overrideMap.set('sections', types.optional(types.array(types.late(():
// IAnyModelType => CourseSection)), []))
const CourseModel = generateType(schema.getModel('course') /* , overrideMap */)
    .views((self) => ({
    isAsset() {
        return false;
    },
    isCourse() {
        return true;
    },
    isDocument() {
        return false;
    },
    isProgram() {
        return false;
    },
    isMeeting() {
        return false;
    },
    isMicrocast() {
        return false;
    },
    itemImage() {
        // console.log('itemImage caled:', self.courseImage);
        return self.courseImage;
    },
    isAncestor(child, possibleParent) {
        let parent = getParent(child);
        while (parent) {
            if (parent === possibleParent) {
                // console.log('Is an ancestor', { child: child.name, parent: possibleParent.name });
                return true;
            }
            parent = isRoot(parent) ? null : getParent(parent);
        }
        // console.log('Not an ancestor', {child: child.name, parent: possibleParent.name});
        return false;
    },
    findSection(id) {
        return self.sections.find((s) => s.findSection(id));
    },
    findItemById: (itemId) => {
        const recursiveFind = (sections, id) => {
            // eslint-disable-next-line no-restricted-syntax
            for (const section of sections) {
                if (section.id === id) {
                    return { type: 'section', section };
                }
                if (section.item && section.item.id === id) {
                    return { type: 'item', section };
                }
                if (section.item?.type) {
                    if (section.item.type === 'microcast' && section.item.microcast?.id === id) {
                        return { type: 'item', section };
                    }
                    if (section.item.asset?.id === id) {
                        return { type: 'item', section };
                    }
                }
                if (section.sections && section.sections.length > 0) {
                    const result = recursiveFind(section.sections, id);
                    if (result) {
                        return result;
                    }
                }
            }
            return null;
        };
        return recursiveFind(self.sections, itemId);
    },
    findFirstNonContainerItem: (section) => {
        if (section.type !== 'container') {
            return section;
        }
        if (section.sections && section.sections.length > 0) {
            // eslint-disable-next-line no-restricted-syntax
            for (const subSection of section.sections) {
                const result = self.findFirstNonContainerItem(subSection);
                if (result) {
                    return result;
                }
            }
        }
        return undefined;
    },
}))
    .actions((self) => ({
    addSubSection(name) {
        const newSection = CourseSection.create({
            id: v4(),
            name,
            type: 'container',
            description: '',
        });
        self.sections.push(newSection);
        self.addChange();
        return newSection;
    },
    addSection(name, type) {
        const item = getNewItem(name, type);
        const newSection = CourseSection.create({
            id: v4(),
            name,
            type,
            item,
            description: '',
        });
        self.sections.push(newSection);
        self.addChange();
        return newSection;
    },
    moveSubSection(subSection, newIndex) {
        const currentIndex = self.sections.indexOf(subSection);
        const adjustedNewIndex = newIndex > currentIndex ? newIndex - 1 : newIndex;
        const tempArray = self.sections.slice();
        moveItem(tempArray, currentIndex, adjustedNewIndex);
        self.sections.replace(tempArray);
        self.addChange();
    },
    /**
     * Moves a section to a new location in the course tree.
     *
     * @param sectionToMove
     * @param target
     * @param index
     */
    moveSection(sectionToMove, newParent, newIndex) {
        // if moving within the same list - need to adjust the index
        const currentParent = sectionToMove.getParent();
        if (currentParent.id === newParent.id) {
            currentParent.moveSubSection(sectionToMove, newIndex);
        }
        else {
            currentParent.removeSubSection(sectionToMove);
            newParent.insertSubSection(sectionToMove, newIndex);
        }
    },
}))
    .views((self) => ({
    calcLessonCount() {
        return self.sections.reduce((total, section) => (total + section.calcLessonCount()), 0);
    },
    calcQuizCount() {
        return self.sections.reduce((total, section) => total + section.calcQuizCount(), 0);
    },
    hasCertification() {
        return self.sections.reduce((total, section) => total + section.hasCertification(), 0);
    },
    hasMaterials() {
        return self.sections.reduce((total, section) => total + section.hasMaterials(), 0);
    },
    /**
     * Determines if the current user has access to take this course. Access
     * is based on user's access groups, plus purchasedCourses, plus course
     * properties.
     * @param user
     */
    checkAccess() {
        return checkAccess(self);
    },
    /**
     * Returns true if the newParent is allowed to receive
     * the sectionToMove. Called by DND on CourseOutline
     * @param sectionToMove R
     * @param newParent
     * @param newIndex
     */
    canReceive(sectionToMove, newParent, newIndex) {
        // if moving within the same list - need to adjust the index
        const currentParent = sectionToMove.getParent();
        const currentIndex = currentParent.sections.indexOf(sectionToMove);
        // if a section has an item, into cant receive items or subsections
        if (!newParent.canHaveItems()) {
            return false;
        }
        // already here
        if (currentParent.id === newParent.id
            && (newIndex === currentIndex || newIndex === currentIndex + 1)) {
            return false;
        }
        // cant move to yourself
        if (sectionToMove === newParent) {
            return false;
        }
        // cant move a node to one if its children
        if (self.isAncestor(newParent, sectionToMove)) {
            return false;
        }
        return true;
    },
    checkoutUrl() {
        // @todo - where should the portal root come from?
        return `checkout?type=course&id=${self.id}`;
    },
    getCertificationSections() {
        return self.filterSections('certification');
    },
    getQuizSections() {
        return self.filterSections('quiz');
    },
    getQuizSectionById(key) {
        const dfs = (secs) => {
            /* depth‑first search: match either the section id itself OR the id of its item */
            // eslint-disable-next-line no-restricted-syntax
            for (const sec of secs) {
                if (sec.id === key || sec.item?.id === key) {
                    return sec;
                }
                if (sec.sections?.length) {
                    const found = dfs(sec.sections);
                    if (found)
                        return found;
                }
            }
            return null;
        };
        return dfs(self.sections);
    },
    getQuestionById(sectionOrItemId, questionId) {
        const hit = self.findItemById(sectionOrItemId);
        if (!hit)
            return null;
        const { section } = hit;
        const questions = section?.item?.questions ?? section?.questions;
        return questions?.find((q) => q?.id === questionId) ?? null;
    },
}));
const saveToS3 = async (rootModel, key, content) => {
    console.log('saveToS3 called', { repo: rootModel.currentRepository, key, content });
    const repository = rootModel.currentRepository;
    await putContent(repository.bucket, key, content, 'text/plain', (progress) => {
        console.log('progress:', progress);
    });
    return {
        slate: {
            s3: {
                repository: repository.key,
                bucket: repository.bucket,
                key,
            },
        },
    };
};
export const Course = types
    .compose(CourseModel, Item).views(() => ({
    canHaveSubSections() {
        return true;
    },
}))
    .actions((self) => ({
    async preUpdate(isNew) {
        self.setInstuctorNames();
        console.log('Course.preUpdate called', { self, isNew });
        if (self.sections.length === 1 && self.sections[0]?.type?.startsWith('scorm')) {
            self.format = 'scorm12';
        }
        else {
            self.format = 'standard';
        }
        if (!self.manualSlug) {
            self.slug = slugify(self.name);
        }
        const slugValidator = fieldValidator('slug', {
            type: 'string',
            validation: 'slug',
        }, false);
        try {
            slugValidator?.validateSync(self.slug, { abortEarly: false });
        }
        catch (error) {
            if (error instanceof Yup.ValidationError) {
                console.error('Validation error:', error.errors);
                navStore.errorDialog.showError('Validation Error', `slug: ${error.message}`);
            }
            throw error;
        }
        if (Array.isArray(self.prerequisiteCertificates)) {
            const newPrerequisites = self.prerequisiteCertificates.map((prerequisite) => ({
                ...prerequisite,
                course: prerequisite.course?.key || null,
                microcast: prerequisite.microcast?.key || null,
            }));
            self.updateValues({
                prerequisiteCertificates: newPrerequisites,
            });
        }
        try {
            const sizeInBytes = (json) => (json ? JSON.stringify(json).length : 0);
            console.log('check for content longer than 1kb');
            const contentPromises = flattenSections(self).map(async (section) => {
                if (!section.item) {
                    return 0;
                }
                const contentSize = sizeInBytes(section.item?.content);
                if (contentSize > 1024) {
                    console.log('content size:', contentSize, 'for section:', section.name);
                    const rootModel = getRoot(self);
                    const key = `richtext/course/${self.id}/item/${section.item.id}/content`;
                    const pointer = await saveToS3(rootModel, key, JSON.stringify(section.item.content));
                    console.log('saved to s3', { key, content: section.item.content });
                    section.updateFields(['item.content'], {
                        'item.content': pointer,
                    });
                    console.log('updated section', section.item.content);
                }
                return contentSize;
            });
            const content = await Promise.all(contentPromises);
            console.log('content:', content);
        }
        catch (error) {
            console.error('Error saving content to s3:', error);
            throw new Error('Error saving rich text content.');
        }
    },
}))
    .named('Course');
export const Certificate = generateType(schema.getModel('certificate') /* , overrideMap */);
export const Certification = generateType(schema.getModel('certification') /* , overrideMap */);
export const Review = generateType(schema.getModel('review') /* , overrideMap */);
export const FAQ = generateType(schema.getModel('faq') /* , overrideMap */);
export const Prerequisite = generateType(schema.getModel('prerequisite') /* , overrideMap */);
export const CourseProgress = generateType(schema.getModel('courseProgress'))
    .views((self) => ({
    getItem() {
        return self.course;
    },
}));
export const trackCourseItemProgressGQL = (isPortalApi) => (`
  mutation TrackCourseItemProgress(
    $courseKey: String!
    $courseItemID: String!
    $progress: String!
    $lastPosition: Int
    $maxPosition: Int
    ${!isPortalApi ? '$organizationID: String!' : ''}
  ) {
    trackCourseItemProgress(
      courseKey: $courseKey
      courseItemID: $courseItemID
      progress: $progress
      lastPosition: $lastPosition
      maxPosition: $maxPosition
      ${!isPortalApi ? 'organizationID: $organizationID' : ''}      
    ) {
      statusCode
      courseProgress {
        ${schema.getGqlPropertyList('courseProgress', isPortalApi, true, false).join(' ')}
      }
      programProgresses {
        ${schema.getGqlPropertyList('programProgress', isPortalApi, true, false).join(' ')}
      }
      message
    }
  }
`);
export const startCourseGQL = (isPortalApi) => (`
  mutation StartCourse(
    $courseKey: String!
    $portalID: String!
    ${!isPortalApi ? '$organizationID: String!' : ''}
  ) {
    startCourse(
      courseKey: $courseKey
      portalID: $portalID
      ${!isPortalApi ? 'organizationID: $organizationID' : ''}      
    ) {
      statusCode
      courseProgress {
        ${schema.getGqlPropertyList('courseProgress', isPortalApi, true, false).join(' ')}
      }
      programProgresses {
        ${schema.getGqlPropertyList('programProgress', isPortalApi, true, false).join(' ')}
      }
      message
    }
  }
`);
export const scormCommitGQL = (isPortalApi) => (`
  mutation ScormCommit(
    $commitData: ScormCommitInput!
    ${!isPortalApi ? '$organizationID: String!' : ''}
  ) {
    scormCommit(
      commitData: $commitData
      ${!isPortalApi ? 'organizationID: $organizationID' : ''}      
    ) {
      statusCode
      message
      courseProgress {
        ${schema.getGqlPropertyList('courseProgress', isPortalApi, true, false).join(' ')}
      }
      microcastProgress {
        ${schema.getGqlPropertyList('microcastProgress', isPortalApi, true, false).join(' ')}
      }
    }
  }
`);
export const getSignedCertificateUrl = (isPortalApi) => (`
  query GetSignedCertificateUrl(
    $certificateID: ID!
    ${!isPortalApi ? '$organizationID: ID!' : ''}
  ) {
    getSignedCertificateUrl(
      certificateID: $certificateID
      ${!isPortalApi ? 'organizationID: $organizationID' : ''}      
    ) {
      statusCode
      url
    }
  }
`);
export const previewCertificate = (isPortalApi) => (`
  mutation PreviewCertificate(
    $organizationID: ID!
    $certificationID: ID!
    $fields: [CertificateFieldInput!]!
    $courseKey: String
    $programKey: String
  ) {
    previewCertificate(
      organizationID: $organizationID
      certificationID: $certificationID
      fields: $fields
      courseKey: $courseKey
      programKey: $programKey
    ) {
      statusCode
      url
    }
  }
`);
//# sourceMappingURL=CourseModel.js.map